import { Box, Divider } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { GridColDef } from "@mui/x-data-grid-pro-6";
import React  from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { StoreType } from "@/ts/utils/types";

import { Error } from "../../../../export/attendance-reports/index";
import useColumnSelector from "../../../design/column-selector/controllers/use-column-selector";
import useTimeOffBalancesReportColumn from "../controllers/use-time-off-balances-report-column";
import useTimeOffBalancesReportController from "../controllers/use-time-off-balances-report-controller";
import useTimeOffBalancesReportData from "../controllers/use-time-off-balances-report-data";
import {resetUpdateBalance} from "../store/time-off-balances-report-slice";
import { TimeOffBalance } from "../store/time-off-balances-report-types";
import TableHeader from "./table-header";
import TimeOffBalancesReportTable from "./time-off-balances-report-table";
import UpdateLeaveBalanceDialog from "./update-leave-balance-dialog";

const TimeOffBalancesReportView = ({
                                     canUpdateLeaveBalance
                                   }: {
  canUpdateLeaveBalance: boolean;
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [
        { reportData, searchText, getReportStatus, open,
          editDialogOpen,
          selectedRowForEdit,
          filterOptions,
          dataRelations
        },
        { setSearchText, setFilterOpen,
          handleEditClick,
          handleEditClose,
          handleUpdateConfirm
        },
    ] = useTimeOffBalancesReportController();

    const { balances: initialData, updateBalanceStatus, updateBalanceErrorMessage} = useTimeOffBalancesReportData();

    const isBadgeNumberEnabled = useSelector(
        (state: StoreType) => state.user.userData.badgeNumberEnabled );

    const columns = useTimeOffBalancesReportColumn(
      searchText,
      isBadgeNumberEnabled,
      canUpdateLeaveBalance,
      handleEditClick,
      );
    const columnsWithInitialState = columns.map(col => ({ ...col, checked: true }));
    const [{ selectedColumns, pickerColumns }, { handleSelectColumns }]: any =
        useColumnSelector([], columnsWithInitialState);

    const filteredColumns = columns.filter((column: GridColDef) =>
        selectedColumns?.includes(column.field)
    );

    if (getReportStatus === "error")
        return <Error errorMessage={t("common_error-occurred")} />;

     const exportData = reportData?.map((row: TimeOffBalance) => {
        const data: { [key: string]: any } = {
            [t("time-off-balances_worker-id")]: row.companyEmployeeId,
           };
        if (isBadgeNumberEnabled) {
            data[t("time-off-balances_badge-number")] = row.badgeNumber ?? "-";
        }
        data[t("time-off-balances_full-name")] = row.fullName;
        data[t("time-off-balances_time-off-type")] = row.timeOffType;
        data[t("time-off-balances_balance")] = row.balanceFormatted;
        data[t("time-off-balances_job-title")] = row.jobTitle ?? "-";
        data[t("time-off-balances_work-center")] = row.workcenter ?? "-";
        data[t("time-off-balances_location")] = row.location ?? "-";
        data[t("time-off-balances_shift-pattern")] = row.shiftPattern ?? "-";
        data[t("time-off-balances_reporting-manager")] = row.reportingManager ?? "-";
        return data;
        });

    return (
      <>
        <Divider sx={{ mt: 2 }} />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <TableHeader
            search={searchText}
            setSearch={setSearchText}
            columnSelector={pickerColumns.filter(
              (column: { field: string }) => column.field !== "actions"
            )}
            handleSelectColumns={handleSelectColumns}
            exportData={exportData}
            open={open}
            setFilterOpen={setFilterOpen}
          />
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <TimeOffBalancesReportTable
              rows={reportData ?? []}
              initialData={initialData ?? []}
              columns={filteredColumns}
              getReportStatus={getReportStatus}
              open={open}
              canUpdateLeaveBalance={canUpdateLeaveBalance}
            />
          </Box>
        </Box>
        {canUpdateLeaveBalance && (
          <UpdateLeaveBalanceDialog
            open={editDialogOpen}
            onClose={handleEditClose}
            rowData={selectedRowForEdit}
            allRows={reportData ?? []}
            availableTimeOffTypes={filterOptions?.timeOffTypes ?? []}
            onConfirm={handleUpdateConfirm}
            isBadgeNumberEnabled={isBadgeNumberEnabled}
            filterRelationships={dataRelations ?? []}
            allLocations={filterOptions?.locations ?? []}
          />
        )}
        {updateBalanceStatus === "fulfilled" && (
          <Snackbar
            open={updateBalanceStatus === "fulfilled"}
            autoHideDuration={2000}
            onClose={() => dispatch(resetUpdateBalance())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="success">
              {t("time-off-balances_balance-updated-successfully")}
            </MuiAlert>
          </Snackbar>
        )}
        {updateBalanceStatus === "error" && (
          <Snackbar
            open={updateBalanceStatus === "error"}
            autoHideDuration={2000}
            onClose={() => dispatch(resetUpdateBalance())}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert elevation={6} variant="filled" severity="error">
              {updateBalanceErrorMessage?.toString() ?? t("common_error-occurred")}
            </MuiAlert>
          </Snackbar>
        )}
      </>
    );
};

export default TimeOffBalancesReportView;